/* 
*   Accessible non-modal dialog windows
*
*   This content is based on the W3C Software used for aria.dialog.js
*
*   Automatically creates open and close listeners based on classes.
*   Uses existing modal classes for styling
*
*   To use this:
*       1. include aria.utils.js file
*       2. include this file
*       3. create infobox trigger(s)
*            a. each trigger element must have 'data-infobox-id' attribute with the of infobox to display as its value
*            b. <button data-infobox-id="unique-infobox-id" aria-label="Get information on X, has pop up dialog" type="button"><i class="fa fa-question-circle"></i></button>
*            c. <a></a> are ok, too - there is an event.preventDefault()
*       4. create infobox div(s) 
*            a. <div class="infobox hidden" role="dialog" id="unique-infobox-id" aria-label="Aria title here, or use aria-labelledby to reference content inside infobox" aria-modal="true">
*            b. close element (button, icon, whatever) must have "infobox-close" class
*               <button class="infobox-close close-icon" id="serial-number-close" aria-label="Close">{% include 'icon' with 'close' %}</button>
*            c. set tabindex=-1 on the main modal-content div or a sensible element near the top of the modal so it is the first focusable element
*            d. have had issues with tabindex values being removed in staged and prod - may need to modify the on-page script to set tabindex and then focus
*       5. style infobox div(s)
*            a. DO NOT EVER set display: none in the modal div css. Use class "hidden" instead (global style)
*            b. infobox should cover entire screen on mobile
*            c. infobox should automatically locate near the trigger on desktop
*            d. have not made styles global because not sure what else will break - see product registration form for example styles
*       6. include <noscript></noscript> fallback text for those without JS
*            a. global style .noscript-text makes font size 95%
*     ** not using aria-haspopup because screen reader support very spotty as of 3/12/2021 https://html5accessibility.com/stuff/2021/02/02/haspopup-haspoop/
*   
*/

(function () {
    /*
     * When util functions move focus around, set this true so the focus listener
     * can ignore the events.
     */
    aria.Utils.IgnoreUtilFocusChanges = false;
  
    // not needed - this block scrolling on body and we aren't doing that here
    //aria.Utils.dialogOpenClass = 'has-dialog';
  
  
    /* Modals can open modals. Keep track of them with this array. */
    aria.OpenDialogList = aria.OpenDialogList || new Array(0);
  
    /**
     * @returns the last opened dialog (the current dialog)
     */
    if (!aria.getCurrentDialog) {
      aria.getCurrentDialog = function () {
        if (aria.OpenDialogList && aria.OpenDialogList.length) {
          return aria.OpenDialogList[aria.OpenDialogList.length - 1];
        }
      };
    }
  
    if (!aria.closeCurrentDialog) {
      aria.closeCurrentDialog = function () {
        var currentDialog = aria.getCurrentDialog();
        if (currentDialog) {
          currentDialog.close();
          return true;
        }
  
        return false;
      };
    }
  
    if (!aria.handleEscape) {
      aria.handleEscape = function (event) {
        var key = event.which || event.keyCode;
  
        if (key === aria.KeyCode.ESC && aria.closeCurrentDialog()) {
          event.stopPropagation();
        }
      };
      document.addEventListener('keyup', aria.handleEscape);
    }
  
    /**
     * @constructor
     * @desc Dialog object providing modal focus management.
     *
     * Assumptions: The element serving as the dialog container is present in the
     * DOM and hidden. The dialog container has role='dialog'.
     *
     * @param dialogId
     *          The ID of the element serving as the dialog container.
     * @param focusAfterClosed
     *          Either the DOM node or the ID of the DOM node to focus when the
     *          dialog closes.
     * @param focusFirst
     *          Optional parameter containing either the DOM node or the ID of the
     *          DOM node to focus when the dialog opens. If not specified, the
     *          first focusable element in the dialog will receive focus.
     */
    aria.Infobox = function (dialogId, focusAfterClosed, focusFirst) {
      this.dialogNode = document.getElementById(dialogId);
      if (this.dialogNode === null) {
        throw new Error('No element found with id="' + dialogId + '".');
      }
  
      var validRoles = ['dialog', 'alertdialog'];
      var isDialog = (this.dialogNode.getAttribute('role') || '')
        .trim()
        .split(/\s+/g)
        .some(function (token) {
          return validRoles.some(function (role) {
            return token === role;
          });
        });
      if (!isDialog) {
        throw new Error(
          'Dialog() requires a DOM element with ARIA role of dialog or alertdialog.');
      }
  
      /* Do we really want to set focus when closed? If non-modal then they may have moved on, but
      if they haven't then we need to focus on something */
      if (typeof focusAfterClosed === 'string') {
        this.focusAfterClosed = document.getElementById(focusAfterClosed);
      }
      else if (typeof focusAfterClosed === 'object') {
        this.focusAfterClosed = focusAfterClosed;
      }
      else {
        throw new Error(
          'the focusAfterClosed parameter is required for the aria.Infobox constructor.');
      }
  
      if (typeof focusFirst === 'string') {
        this.focusFirst = document.getElementById(focusFirst);
      }
      else if (typeof focusFirst === 'object') {
        this.focusFirst = focusFirst;
      }
      else {
        this.focusFirst = null;
      }
  
      /* do we need this for infobox? this is typically modal behavior */
      // Bracket the dialog node with two invisible, focusable nodes.
      // While this dialog is open, we use these to make sure that focus never
      // leaves the document even if dialogNode is the first or last node.
      /*
      var preDiv = document.createElement('div');
      this.preNode = this.dialogNode.parentNode.insertBefore(preDiv,
        this.dialogNode);
      this.preNode.tabIndex = 0;
      var postDiv = document.createElement('div');
      this.postNode = this.dialogNode.parentNode.insertBefore(postDiv,
        this.dialogNode.nextSibling);
      this.postNode.tabIndex = 0;
      */
  
      // If this modal is opening on top of one that is already open,
      // get rid of the document focus listener of the open dialog.
      if (aria.OpenDialogList.length > 0) {
        aria.getCurrentDialog().removeListeners();
      }
  
      this.addListeners();
      aria.OpenDialogList.push(this);
      this.clearDialog();
  
      // make visible
      this.dialogNode.classList.add('default_dialog');
      this.dialogNode.classList.remove('hidden');
  
      // only reposition infobox on desktop - for mobile it will take up entire screen as modal does
      var isMobile = window.matchMedia("only screen and (max-width: 768px)").matches;
      if (!isMobile) {
        // clicked element is focusAfterClosed - get it's position and put the infobox near it
        var x = focusAfterClosed.getBoundingClientRect().left + window.scrollX  ;
        var y = focusAfterClosed.getBoundingClientRect().top + window.scrollY + focusAfterClosed.getBoundingClientRect().height ;
        this.dialogNode.style.top = y + "px";
        this.dialogNode.style.left = x + "px";
      }
  
      // set position and z-index in case we're stacking windows - background overlay z-index is 500
      this.dialogNode.style.zIndex = 500 + aria.OpenDialogList.length;
      this.dialogNode.style.position = 'absolute';   
  
      if (this.focusFirst) {
        this.focusFirst.focus();
      }
      else {
        aria.Utils.focusFirstDescendant(this.dialogNode);
      }
  
      this.lastFocus = document.activeElement;
  
    }; // end Dialog constructor
  
    aria.Infobox.prototype.clearDialog = function () {
      Array.prototype.map.call(
        this.dialogNode.querySelectorAll('input'),
        function (input) {
          input.value = '';
        }
      );
    };
  
    /**
     * @desc
     *  Hides the current top dialog,
     *  removes listeners of the top dialog,
     *  restore listeners of a parent dialog if one was open under the one that just closed,
     *  and sets focus on the element specified for focusAfterClosed.
     */
    aria.Infobox.prototype.close = function () {
      aria.OpenDialogList.pop();
      this.removeListeners();
      // aria.Utils.remove(this.preNode);
      // aria.Utils.remove(this.postNode);
      
      // hide
      this.dialogNode.classList.add('hidden');
      this.dialogNode.classList.remove('default_dialog');
      /* do we really need this? not a modal so might have moved on */
      this.focusAfterClosed.focus();
  
      // If a dialog was open underneath this one, restore its listeners.
      if (aria.OpenDialogList.length > 0) {
        aria.getCurrentDialog().addListeners();
      }
      else {
        document.body.classList.remove(aria.Utils.dialogOpenClass);
      }
    }; // end close
  
    /**
     * @desc
     *  Hides the current dialog and replaces it with another.
     *
     * @param newDialogId
     *  ID of the dialog that will replace the currently open top dialog.
     * @param newFocusAfterClosed
     *  Optional ID or DOM node specifying where to place focus when the new dialog closes.
     *  If not specified, focus will be placed on the element specified by the dialog being replaced.
     * @param newFocusFirst
     *  Optional ID or DOM node specifying where to place focus in the new dialog when it opens.
     *  If not specified, the first focusable element will receive focus.
     */
    aria.Infobox.prototype.replace = function (newDialogId, newFocusAfterClosed, newFocusFirst) {
      var closedDialog = aria.getCurrentDialog();
      aria.OpenDialogList.pop();
      this.removeListeners(); 
      // aria.Utils.remove(this.preNode);
      // aria.Utils.remove(this.postNode);
  
      // hide
      this.dialogNode.classList.add('hidden');
      this.dialogNode.classList.remove('default_dialog');
  
      var focusAfterClosed = newFocusAfterClosed || this.focusAfterClosed;
      var dialog = new aria.Infobox(newDialogId, focusAfterClosed, newFocusFirst);
    }; // end replace
  
    aria.Infobox.prototype.addListeners = function () {
      document.addEventListener('focus', this.trapFocus, true);
    }; // end addListeners
  
    aria.Infobox.prototype.removeListeners = function () {
      document.removeEventListener('focus', this.trapFocus, true);
    }; // end removeListeners
  
    /* do we need to trap focus? */
    /*
    aria.Infobox.prototype.trapFocus = function (event) {
      if (aria.Utils.IgnoreUtilFocusChanges) {
        return;
      }
      var currentDialog = aria.getCurrentDialog();
      if (currentDialog.dialogNode.contains(event.target)) {
        currentDialog.lastFocus = event.target;
      }
      else {
        aria.Utils.focusFirstDescendant(currentDialog.dialogNode);
        if (currentDialog.lastFocus == document.activeElement) {
          aria.Utils.focusLastDescendant(currentDialog.dialogNode);
        }
        currentDialog.lastFocus = document.activeElement;
      }
    }; // end trapFocus
    */
  
    window.openInfobox = function (dialogId, focusAfterClosed, focusFirst) {
      var dialog = new aria.Infobox(dialogId, focusAfterClosed, focusFirst);
    };
  
    window.closeInfobox= function (closeButton) {
      var topDialog = aria.getCurrentDialog();
      if (topDialog.dialogNode.contains(closeButton)) {
        topDialog.close();
      }
    }; // end closeInfobox
  
    window.replaceDialog = function (newDialogId, newFocusAfterClosed,
      newFocusFirst) {
      var topDialog = aria.getCurrentDialog();
      if (topDialog.dialogNode.contains(document.activeElement)) {
        topDialog.replace(newDialogId, newFocusAfterClosed, newFocusFirst);
      }
    }; // end replaceDialog
  
  }());
  
  // add listeners after content load
  document.addEventListener( "DOMContentLoaded", function() {
    // infobox open listeners - element must have 'data-infobox-id' attribute - value is id of modal to display
    document.querySelectorAll('[data-infobox-id]').forEach(function(item){ 
      item.addEventListener('click', function(e) {
        e.preventDefault();
        openInfobox(item.getAttribute("data-infobox-id"),this);
      });
    });
  
    // infobox close listeners - element must have 'close' class
    document.querySelectorAll('.infobox-close').forEach(function(item) {
        item.addEventListener('click', function(e){
          e.preventDefault();
          closeInfobox(item);
        });
    });
  
    // close infobox when user clicks on something outside it
    document.body.addEventListener('click', function(e) {
      // check if target or target parent has data-infobox-id, meaning we're opening an infobox
      // checking target and parent because button content is the target instead of the button
      if(e.target.getAttribute('data-infobox-id') || e.target.parentElement.getAttribute('data-infobox-id')) {
        return;
      }
      document.querySelectorAll('.infobox.default_dialog').forEach(function(item) {
        if(!item.contains(e.target) ) {
          closeInfobox(item);
        }
      })
    })
  });
  